import { isMobile } from 'react-device-detect';

export type CurrencyType = 'game' | 'player';

export type ContentfulType = {
    spaceId: string;
    environmentId: string;
    accessToken: string;
};

export type SentryType = {
    trace: string;
    dsn: string;
};

export type SiteDataType = {
    contentful: ContentfulType;
    sentry: SentryType;
};

export type SiteType = {
    siteCode: string;
    siteId: number;
    homeUrl: string;
    helpUrl: string;
    responsibleUrl: string;
    selfExclusionUrl?: string;
    ecomUrl?: string;
    gamePayoutsUrl?: string;
    gameHelpUrl?: string;
    data: Partial<SiteDataType>;
};

export type DisplayType = {
    enableAutoBuyLossLimit: boolean;
    showTotalTickets: boolean;
    defaultCurrency: CurrencyType;
    enableCurrencyToggle: boolean;
    enableFreeBuyConfirm: boolean;
    enableUnlimitedAutoBuy: boolean;
    enableMaxCardAutoBuy: boolean;
    autoBuyLossLimitAmount: number;
    autoBuyGameLimit: number;
    consumeEntitlementsFirst: boolean;
};

export type RoomType = {
    skin: string;
    announcer: string;
    caller: string;
    locale: string;
    localeId: number;
    gameId: string;
    logoUrl?: string;
    qrCodeBaseUrl?: string;
};

export type ScreenConfigType = {
    site: SiteType;
    display: DisplayType;
    room: RoomType;
    casinoAssetsURL: string;
};

export type SideGamesType = {
    name: string;
    type: string;
    gameType?: string;
    configId: number;
    style: string;
    launch: string;
    thumbnailUrl?: string;
};

export type ScreenConfigState = {
    env: string;
    screenConfig: ScreenConfigType;
    sideGames: SideGamesType[];
    language: string;
};

export class ScreenConfigRequestError extends Error {
    status: number;

    constructor(status: number) {
        super(`Screen config request failed with status ${status}`);
        this.name = 'ScreenConfigRequestError';
        this.status = status;
    }
}

export const normalizeDefaultCurrency = (value: unknown): CurrencyType => {
    if (typeof value !== 'string') {
        return 'game';
    }

    const currency = value.trim().toLowerCase();

    return currency === 'player' ? 'player' : 'game';
};

export const normalizeScreenConfig = (
    config: ScreenConfigState
): ScreenConfigState => ({
    ...config,
    screenConfig: {
        ...config.screenConfig,
        display: {
            ...config.screenConfig.display,
            defaultCurrency: normalizeDefaultCurrency(
                config.screenConfig.display.defaultCurrency
            ),
        },
    },
});

export const fetchScreenConfig = (() => {
    let promise: Promise<ScreenConfigState>;

    return () => {
        if (!promise) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('MOBILE', isMobile ? 'yes' : 'no');

            promise = fetch(`/screen-config?${searchParams.toString()}`).then(
                (response) => {
                    if (!response.ok) {
                        throw new ScreenConfigRequestError(response.status);
                    }

                    return (response.json() as Promise<ScreenConfigState>).then(
                        normalizeScreenConfig
                    );
                }
            );
        }

        return promise;
    };
})();
